/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useMemo } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Select, Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import i18next from 'i18next';
import type { VisitFormData } from './schema';
import { TIME_SLOTS } from './schema';
import { labelStyle } from './drawer-styles';
import { TimeSlotGroup } from './TimeSlotGroup';

/** Hours of the day, as the two-digit strings the form stores. */
const HOURS: { value: string; label: string }[] = Array.from(
  { length: 24 },
  (_, h) => ({ value: String(h).padStart(2, '0'), label: String(h).padStart(2, '0') }),
);

/**
 * Minutes in five-minute steps.
 *
 * A visit is agreed on the phone as "half past three", not as 15:37, so the
 * full sixty entries would be sixty things to scroll past to reach the
 * dozen anyone picks. A time the chips above already offer is reachable
 * here too -- this is the escape hatch, not a competing list.
 */
const MINUTES: { value: string; label: string }[] = Array.from(
  { length: 12 },
  (_, i) => {
    const m = String(i * 5).padStart(2, '0');
    return { value: m, label: m };
  },
);

/**
 * Split a stored ``HH:MM`` into its parts, tolerating an empty value.
 *
 * @param value - The stored time, possibly empty.
 * @returns The hour and minute parts, empty strings when unset.
 */
function splitTime(value: string): { hour: string; minute: string } {
  const [hour = '', minute = ''] = (value || '').split(':');
  return { hour, minute };
}

interface DateTimeSectionProps {
  control: Control<VisitFormData>;
  occupiedSlots: Set<string>;
}

/**
 * Builds the next 7 calendar days as date chips, localized to the active locale.
 *
 * @returns An array of `{ label, weekday, key }` entries, one per day.
 */
function getNext7Days(): { label: string; weekday: string; key: string }[] {
  const locale = i18next.language || 'pt-BR';
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return {
      label: String(d.getDate()).padStart(2, '0'),
      weekday: d.toLocaleDateString(locale, { weekday: 'short' }),
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    };
  });
}

const MORNING_SLOTS = TIME_SLOTS.filter((s) => parseInt(s, 10) < 12);
const AFTERNOON_SLOTS = TIME_SLOTS.filter((s) => parseInt(s, 10) >= 12);

/**
 * Date chip strip and morning/afternoon time slots for the visit drawer.
 *
 * @param props - The form control and the set of already-occupied time slots.
 * @returns The date and time selection section.
 */
export function DateTimeSection({ control, occupiedSlots }: DateTimeSectionProps) {
  const { t } = useTranslation('features/visits');
  const days = useMemo(() => getNext7Days(), []);
  const [showCustomTime, setShowCustomTime] = useState(false);

  return (
    <div className="flex flex-col gap-(--space-4)">
      <div>
        <Text as="label" style={labelStyle}>{t('drawer.date')}</Text>
        <Controller
          name="visitDate"
          control={control}
          render={({ field }) => (
            <div className="flex gap-(--space-2) overflow-x-auto pb-(--space-1)">
              {days.map((d) => {
                const active = field.value === d.key;
                return (
                  <UnstyledButton
                    key={d.key}
                    type="button"
                    onClick={() => field.onChange(d.key)}
                    className={cn(
                      'flex min-w-[52px] shrink-0 flex-col items-center gap-(--space-0-5)',
                      'rounded-(--radius-md) px-(--space-1) py-(--space-2)',
                      'font-[family-name:var(--font-family)] cursor-pointer',
                      'transition-[var(--transition-standard)]',
                      active
                        ? 'border-[1.5px] border-(--seed-base) bg-(--seed-base) text-(--button-primary-text)'
                        : 'border border-(--border-subtle) bg-(--surface-primary) text-(--text-primary)',
                    )}
                  >
                    {/*
                      Both lines state their colour instead of inheriting it
                      from the button. `Text` falls back to
                      `text-[var(--text-primary)]` whenever it is given no
                      tone, which overrides whatever the parent set — so on
                      the selected chip the day number was painting dark ink
                      on the dark seed fill and disappearing. The weekday
                      above it only looked right by accident: its
                      `text-(--text-xs)` collides with that fallback in
                      tailwind-merge and knocks it out, leaving the
                      inherited colour standing.
                    */}
                    <Text
                      as="span"
                      c={active ? 'var(--button-primary-text)' : 'var(--text-primary)'}
                      className="text-(--text-xs) capitalize opacity-70"
                    >
                      {d.weekday}
                    </Text>
                    <Text
                      as="span"
                      c={active ? 'var(--button-primary-text)' : 'var(--text-primary)'}
                      className="text-sm font-bold"
                    >
                      {d.label}
                    </Text>
                  </UnstyledButton>
                );
              })}
            </div>
          )}
        />
      </div>

      <div>
        <Text as="label" style={labelStyle}>{t('drawer.time')}</Text>
        <Controller
          name="visitTime"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-(--space-3)">
              <TimeSlotGroup
                label={t('drawer.morning')}
                slots={MORNING_SLOTS}
                selectedSlot={field.value}
                occupiedSlots={occupiedSlots}
                onSelect={(slot) => { field.onChange(slot); setShowCustomTime(false); }}
              />
              <TimeSlotGroup
                label={t('drawer.afternoon')}
                slots={AFTERNOON_SLOTS}
                selectedSlot={field.value}
                occupiedSlots={occupiedSlots}
                onSelect={(slot) => { field.onChange(slot); setShowCustomTime(false); }}
              />
              <UnstyledButton
                type="button"
                onClick={() => setShowCustomTime(true)}
                className={cn(
                  'rounded-(--radius-md) px-(--space-3) py-(--space-2) text-center',
                  'text-[length:var(--text-meta)] font-medium font-[family-name:var(--font-family)]',
                  'cursor-pointer transition-[var(--transition-standard)]',
                  showCustomTime
                    ? 'border-[1.5px] border-(--seed-base) bg-(--seed-base) text-(--button-primary-text)'
                    : 'border border-dashed border-(--border-subtle) bg-transparent text-(--text-secondary)',
                )}
              >
                {t('drawer.otherTime')}
              </UnstyledButton>
              {/*
                Two selects rather than an <input type="time">.

                The native control put the whole interaction behind a clock
                icon: the only way to reach a list was to hit that glyph,
                and what opened was the browser's own popup — blue, in the
                browser's language, and impossible to theme. Nothing about
                it belonged to this form.

                These are the same Select the rest of the drawer uses, so
                they inherit the palette, the focus ring and the keyboard
                behaviour, and each one says what it is above itself.
              */}
              {showCustomTime && (
                <div className="flex items-end gap-(--space-3)">
                  <div className="flex-1">
                    <Text as="label" style={labelStyle}>{t('drawer.hour')}</Text>
                    <Select
                      name="visitTimeHour"
                      value={splitTime(field.value).hour}
                      onChange={(val) => {
                        const { minute } = splitTime(field.value);
                        field.onChange(`${String(val ?? '')}:${minute || '00'}`);
                      }}
                      placeholder={t('drawer.hourPlaceholder')}
                      data={HOURS}
                    />
                  </div>
                  <Text
                    as="span"
                    aria-hidden="true"
                    className="pb-(--space-2) text-[length:var(--text-body)] text-(--text-tertiary)"
                  >
                    :
                  </Text>
                  <div className="flex-1">
                    <Text as="label" style={labelStyle}>{t('drawer.minute')}</Text>
                    <Select
                      name="visitTimeMinute"
                      value={splitTime(field.value).minute}
                      onChange={(val) => {
                        const { hour } = splitTime(field.value);
                        field.onChange(`${hour || '09'}:${String(val ?? '')}`);
                      }}
                      placeholder={t('drawer.minutePlaceholder')}
                      data={MINUTES}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}
